import { useEffect, useState } from 'react';
import { Delete as DeleteIcon, PhotoCamera as PhotoCameraIcon } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Typography,
} from '@mui/material';
import FileUpload from '../../Common/FileUpload';
import logo from '../../../assets/images/logo-pdf.png';

const MAX_PESO_ARCHIVOS = 4;
const MAX_ARCHIVOS_ADJUNTOS = 4;
const ARCHIVOS_PERMITIDOS = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];

export const StepHistoriaClinica = ({
  esMobile,
  onChangeArchivosAdjuntos,
  onChangeValidStep,
  onError,
}) => {
  const [archivosAdjuntos, setArchivosAdjuntos] = useState([]);
  const [pesoArchivosAdjuntos, setPesoArchivosAdjuntos] = useState(0);
  const [guiaCheckList, setGuiaCheckList] = useState({
    nombre: false,
    antecedente: false,
    infrome: false,
    firma: false,
  });

  const [error, setError] = useState(null);

  useEffect(() => {
    if (error && onError) {
      onError(error);
    }

    return () => {
      setError(null);
    };
  }, [error]);

  const onChangeDocumentList = (list) => {
    if (onChangeArchivosAdjuntos) {
      onChangeArchivosAdjuntos(list);
    }

    if (onChangeValidStep) {
      onChangeValidStep(Object.values(guiaCheckList).every((i) => i === true) && list.length >= 1);
    }
  };

  const onChangeGuiaCheckList = (newList = {}) => {
    setGuiaCheckList({ ...newList });
    if (onChangeValidStep) {
      onChangeValidStep(
        Object.values(newList).every((i) => i === true) && archivosAdjuntos.length >= 1,
      );
    }
  };

  const onDeleteArchivo = (id) => {
    const archivoEliminado = archivosAdjuntos.find((i) => i.id === id);
    const archivosAdjuntosNewList = archivosAdjuntos.filter((i) => i.id !== archivoEliminado.id);
    setPesoArchivosAdjuntos(pesoArchivosAdjuntos - bytesToMb(archivoEliminado.dimension));
    setArchivosAdjuntos(archivosAdjuntosNewList);
    onChangeDocumentList(archivosAdjuntosNewList);
  };

  const isValidFileType = (file) => {
    return ARCHIVOS_PERMITIDOS.includes(file.type);
  };

  const bytesToMb = (value) => {
    return value / 1048576;
  };

  const onClickSubirArchivo = (event) => {
    if (archivosAdjuntos.length >= MAX_ARCHIVOS_ADJUNTOS) {
      setError('Llegaste al límite de cantidad de archivos que podes cargar.');
    } else {
      const files = Array.from(event.target.files);

      if (files.length > 0) {
        const file = files[0];

        if (file && !isValidFileType(file)) {
          setError('Por favor selecciona un archivo (pdf, jpg, png) válido.');
          return;
        }

        const sizeFile = bytesToMb(file.size) + pesoArchivosAdjuntos;

        if (sizeFile >= MAX_PESO_ARCHIVOS) {
          setError('Superaste el limite de tamaño de archivos que podes cargar.');
        } else {
          convertToBase64(file, true);
        }
      }
    }
  };

  const convertToBase64 = (file, requerido) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const fileExtension = file.type.split('/')[1];
      const sizeMB = bytesToMb(file.size);

      const a = {
        fileName: file.name,
        linkPreview: logo,
        extension: '.' + fileExtension,
        dimension: file.size,
        contentBase64: reader.result.split(',')[1],
        requerido: requerido,
        id: archivosAdjuntos.length || 0,
      };

      const existeArchivo = archivosAdjuntos.find(
        (item) => item.fileName === a.fileName && item.dimension === a.dimension,
      );

      if (existeArchivo) {
        setError('El archivo que está intentando subir ya existe.');
        return;
      }

      setArchivosAdjuntos([...archivosAdjuntos, a]);
      setPesoArchivosAdjuntos(pesoArchivosAdjuntos + sizeMB);
      onChangeDocumentList([...archivosAdjuntos, a]);
    };
  };

  return (
    <>
      <FormControl fullWidth={true} variant='outlined' margin='none'>
        <Typography variant='body' color='textSecondary' component='p' spacing={1}>
          <b>Verificá los siguientes datos de la Historia Clínica ORIGINAL (o copia)</b>
        </Typography>
        <FormGroup sx={{ my: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                onChange={() =>
                  onChangeGuiaCheckList({ ...guiaCheckList, nombre: !guiaCheckList.nombre })
                }
                checked={guiaCheckList.nombre}
              />
            }
            label={
              <Typography component='p' sx={{ fontSize: '14px' }}>
                <b>Nombre, apellido y número de documento</b> del paciente de forma clara.
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                onChange={() =>
                  onChangeGuiaCheckList({
                    ...guiaCheckList,
                    antecedente: !guiaCheckList.antecedente,
                  })
                }
                checked={guiaCheckList.antecedente}
              />
            }
            label={
              <Typography component='p' sx={{ fontSize: '14px' }}>
                <b>Antecedentes</b> patológicos y antecedentes de la enfermedad actual.
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                onChange={() =>
                  onChangeGuiaCheckList({ ...guiaCheckList, infrome: !guiaCheckList.infrome })
                }
                checked={guiaCheckList.infrome}
              />
            }
            label={
              <Typography component='p' sx={{ fontSize: '14px' }}>
                <b>Informes</b> de estudios realizados y <b>diagnóstico</b>.
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                onChange={() =>
                  onChangeGuiaCheckList({ ...guiaCheckList, firma: !guiaCheckList.firma })
                }
                checked={guiaCheckList.firma}
              />
            }
            label={
              <Typography component='p' sx={{ fontSize: '14px' }}>
                <b>Firma y sello</b> legible del médico tratante y <b>logo institucional</b>.
              </Typography>
            }
          />
        </FormGroup>
      </FormControl>
      <Typography variant='body2' color='textSecondary' component='p' spacing={1}>
        Cantidad de archivos:{' '}
        <b>
          {archivosAdjuntos.length} / {MAX_ARCHIVOS_ADJUNTOS}
        </b>
        <br />
        Tamaño de Archivos:{' '}
        <b>
          {pesoArchivosAdjuntos.toFixed(2)}MB / {MAX_PESO_ARCHIVOS}MB
        </b>
      </Typography>
      <Box>
        <ImageList color='neutral' cols={esMobile ? 2 : 5} rowHeight={100} gap={16}>
          {archivosAdjuntos?.length > 0 &&
            archivosAdjuntos.map((i, k) => (
              <ImageListItem key={`${i.fileName}-${k}`}>
                {i.extension === '.pdf' && (
                  <img src={i.linkPreview} alt={i.fileName} style={{ height: '100px' }} />
                )}
                {i.extension !== '.pdf' && (
                  <img
                    src={`data:image;base64,${i.contentBase64}`}
                    alt={i.fileName}
                    style={{ height: '100px' }}
                  />
                )}
                <ImageListItemBar
                  title={i.fileName}
                  key={`${i.fileName}-${k}`}
                  actionIcon={
                    <IconButton
                      value={i.id}
                      aria-label={`Detalle sobre el archivos ${i.fileName}.`}
                      onClick={() => onDeleteArchivo(i.id)}
                    >
                      <DeleteIcon color='error' />
                    </IconButton>
                  }
                />
              </ImageListItem>
            ))}
        </ImageList>
      </Box>

      <FormControl fullWidth={true} variant='outlined' margin='normal' align='center'>
        <FileUpload
          accept={ARCHIVOS_PERMITIDOS.join(',')}
          compressFile
          onChangeUpload={(e) => onClickSubirArchivo(e)}
          color='neutral'
          startIcon={<PhotoCameraIcon />}
          size='medium'
          variant='contained'
          aria-label='Subir archivo'
          disabled={archivosAdjuntos.length >= MAX_ARCHIVOS_ADJUNTOS}
        >
          Subir archivo
        </FileUpload>
      </FormControl>
    </>
  );
};
